import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import CustomModal from "@/components/modal/CustomModal";
import httpClient from "@config/axios";
import { toast } from "@/components/toast/CustomToast";
import {
  buildRedistributeAssignments,
  type RedistributePreview,
  type RosterMember,
} from "@utils/ward-redistribute-util";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";

type Group = { group_id: string; group_name: string };

type Pair = {
  preceptee_id: string;
  preceptee_name?: string;
  preceptor_id: string;
  preceptor_name?: string;
  status: string;
};

// 팀 미리보기 이름 칩 — 이름 + grade 배지
function NameChip({ name, grade }: { name: string; grade?: number | null }) {
  return (
    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
      {name}
      {grade != null && (
        <span className="ml-0.5 text-[10px] text-slate-400">G{grade}</span>
      )}
    </span>
  );
}

// 참여 로스터 rows (옵션1·2 공용) — checkbox | 이름(+N전담) | [병동] | Grade | 프리셉터 | 근무
function RosterRows({
  members,
  isOn,
  onToggle,
  wardName,
}: {
  members: RosterMember[];
  isOn: (m: RosterMember) => boolean;
  onToggle: (nid: string) => void;
  wardName?: (gid?: string) => string | undefined;
}) {
  return (
    <div className="max-h-52 overflow-y-auto rounded border border-slate-200 bg-white">
      <div className="flex items-center gap-2 border-b bg-slate-50 px-2 py-1 text-[11px] font-medium text-slate-500">
        <span className="w-4" />
        <span className="flex-1">이름</span>
        {wardName && <span className="w-16">병동</span>}
        <span className="w-8 text-center">G</span>
        <span className="w-24">프리셉터</span>
        <span className="w-16 text-right">근무</span>
      </div>
      {members.map((n) => {
        const on = isOn(n);
        return (
          <label
            key={n.nurse_id}
            className={`flex cursor-pointer items-center gap-2 border-b border-slate-100 px-2 py-1 text-xs last:border-0 hover:bg-slate-50 ${
              on ? "" : "text-slate-400"
            }`}
          >
            <input
              type="checkbox"
              className="w-4"
              checked={on}
              onChange={() => onToggle(n.nurse_id)}
            />
            <span className="flex-1">
              {n.name}
              {n.is_night && (
                <span className="ml-1 rounded bg-amber-50 px-1 text-[10px] text-amber-600">
                  N전담
                </span>
              )}
            </span>
            {wardName && (
              <span className="w-16 truncate text-slate-500">
                {wardName(n.group_id) ?? "-"}
              </span>
            )}
            <span className="w-8 text-center text-slate-500">
              {n.grade ?? "-"}
            </span>
            <span className="w-24 truncate">
              {n.is_preceptor ? (
                <span className="rounded bg-teal-50 px-1 text-[10px] text-teal-600">
                  프리셉터
                </span>
              ) : n.preceptor_name ? (
                <span className="text-slate-500">↳{n.preceptor_name}</span>
              ) : (
                <span className="text-slate-300">-</span>
              )}
            </span>
            <span
              className={`w-16 text-right ${
                n.shift === "전체" ? "text-slate-400" : "text-slate-600"
              }`}
            >
              {n.shift}
            </span>
          </label>
        );
      })}
    </div>
  );
}

// 옵션1(병동 내 팀분류) preview 응답
type ClassifyPreview = {
  num_teams: number;
  num_pool: number;
  num_changed: number;
  num_excluded_night: number;
  teams: Record<string, string[]>;
  unassigned: { nurse_id: string; name?: string }[];
  pool_roster: RosterMember[];
  warnings?: string[];
  pairs?: Pair[];
};

/**
 * 원티드 기반 재배치 — 단일 진입점.
 *  - 병동 1개 선택 → 옵션1(병동 내 팀 재분류, /teams/classify)
 *  - 병동 2개+ 선택 → 옵션2(병동 간 재분배, /teams/redistribute)
 */
export default function WardRedistributeModal({
  onClose,
  groups,
}: {
  onClose: () => void;
  groups: Group[];
}) {
  const queryClient = useQueryClient();
  // 관리 병동 후보(home + hn_id). 부모의 admin-gated groups 비의존, 백엔드 재검증.
  const { data: managed } = useQuery({
    queryKey: ["my-admin-groups"],
    queryFn: () =>
      httpClient.get<Group[]>("/groups/my-admin-groups"),
  });
  const wardOptions: Group[] = managed && managed.length > 0 ? managed : groups;

  const _next = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1);
  const [month, setMonth] = useState(
    `${_next.getFullYear()}-${String(_next.getMonth() + 1).padStart(2, "0")}`
  );
  const [selected, setSelected] = useState<string[]>([]);
  const [mode, setMode] = useState<"even" | "explicit">("even");
  const [targetSizes, setTargetSizes] = useState<Record<string, number>>({});
  // [옵션B] 팀 없는 병동을 N개로 가분할: {group_id: N}. 정의된 팀이 있으면 백엔드가 무시.
  const [teamCounts, setTeamCounts] = useState<Record<string, number>>({});
  // G1(시니어) 없는 병동도 막지 않고 진행(사용자가 '시니어 없이 진행' 선택 시).
  const [allowMissingG1, setAllowMissingG1] = useState(false);
  const [previewO1, setPreviewO1] = useState<ClassifyPreview | null>(null);
  const [previewO2, setPreviewO2] = useState<RedistributePreview | null>(null);
  const [needsG1, setNeedsG1] = useState<
    { group_id: string; name?: string }[] | null
  >(null);
  // 참여 선택. null = 기본(옵션1: N전담 제외 전원 / 옵션2: 전원). 미참여=미지정(O1)/현재병동(O2).
  const [participantIds, setParticipantIds] = useState<Set<string> | null>(null);
  const [pairDecisions, setPairDecisions] = useState<Record<string, string>>({});

  const isOption1 = selected.length === 1;
  const hasPreview = isOption1 ? !!previewO1 : !!previewO2;

  // 옵션1 참여 로스터는 preview 가 운반(pool_roster) — HN 은 /nurses 로 타 병동
  // nurse 를 못 읽어, 백엔드 preview 가 근무코드까지 담아 보낸다.
  const o1Roster: RosterMember[] = previewO1?.pool_roster ?? [];
  const nameById: Record<string, string> = {};
  o1Roster.forEach((n) => (nameById[n.nurse_id] = n.name));

  const resetResult = () => {
    setPreviewO1(null);
    setPreviewO2(null);
    setNeedsG1(null);
    setParticipantIds(null);
    setPairDecisions({});
    setTeamCounts({});
    setAllowMissingG1(false);
  };
  const toggleWard = (gid: string) => {
    resetResult();
    setSelected((s) =>
      s.includes(gid) ? s.filter((x) => x !== gid) : [...s, gid]
    );
  };

  // 참여 후보(옵션2 풀) — preview 가 pool_roster 로 운반(grade/프리셉터/근무 포함)
  const o2Roster: RosterMember[] = previewO2?.pool_roster ?? [];
  const wardNameById = (gid?: string) =>
    wardOptions.find((w) => w.group_id === gid)?.group_name;
  // 팀 미리보기 칩의 grade 표기용 — pool_roster(옵션1/2) 에서 수집
  const gradeById: Record<string, number | null | undefined> = {};
  o1Roster.forEach((n) => (gradeById[n.nurse_id] = n.grade));
  o2Roster.forEach((n) => (gradeById[n.nurse_id] = n.grade));
  const isParticipant = (nid: string, night: boolean) =>
    participantIds === null ? (isOption1 ? !night : true) : participantIds.has(nid);
  const effectiveSet = () => {
    if (participantIds !== null) return participantIds;
    if (isOption1)
      return new Set(o1Roster.filter((n) => !n.is_night).map((n) => n.nurse_id));
    return new Set(o2Roster.map((r) => r.nurse_id));
  };
  const toggleParticipant = (nid: string) =>
    setParticipantIds(() => {
      const next = new Set(effectiveSet());
      if (next.has(nid)) {
        next.delete(nid);
      } else {
        next.add(nid);
      }
      return next;
    });
  const partCount = () => effectiveSet().size;

  const { mutate: runPreview, isPending: previewing } = useMutation({
    mutationFn: async (vars?: {
      pairs?: Record<string, string>;
      teamCounts?: Record<string, number>;
      allowMissingG1?: boolean;
    }) => {
      const [y, m] = month.split("-").map(Number);
      const pids = participantIds ? Array.from(participantIds) : undefined;
      // setState 비동기 → 호출자가 새 값을 vars 로 직접 운반(없으면 현재 상태).
      const tc = vars?.teamCounts ?? teamCounts;
      const amg = vars?.allowMissingG1 ?? allowMissingG1;
      if (isOption1) {
        const data = await httpClient.post<ClassifyPreview>(
          "/teams/classify/preview",
          {
            year: y,
            month: m,
            group_id: selected[0],
            participant_ids: pids,
            pair_decisions: vars?.pairs ?? pairDecisions,
          }
        );
        return { kind: "o1" as const, data };
      }
      const data = await httpClient.post<RedistributePreview>(
        "/teams/redistribute/preview",
        {
          group_ids: selected,
          year: y,
          month: m,
          capacity_mode: mode,
          target_sizes: mode === "explicit" ? targetSizes : undefined,
          participant_ids: pids,
          team_counts: Object.keys(tc).length ? tc : undefined,
          allow_missing_g1: amg || undefined,
        }
      );
      return { kind: "o2" as const, data };
    },
    onSuccess: (res) => {
      setNeedsG1(null);
      if (res.kind === "o1") {
        setPreviewO1(res.data);
        setPreviewO2(null);
      } else {
        setPreviewO2(res.data);
        setPreviewO1(null);
      }
    },
    onError: (err: unknown) => {
      const detail = (
        err as { response?: { data?: { detail?: { needs_g1_setup?: unknown } } } }
      )?.response?.data?.detail;
      if (detail?.needs_g1_setup) {
        setNeedsG1(detail.needs_g1_setup as { group_id: string; name?: string }[]);
      } else {
        toast.error("미리보기에 실패했습니다.");
      }
    },
  });

  // 짝 유지/해제 → pair_decisions 갱신 후 즉시 재미리보기 (옵션1)
  const setPairDecision = (peId: string, decision: "keep" | "release") => {
    const next = { ...pairDecisions, [peId]: decision };
    setPairDecisions(next);
    if (previewO1) runPreview({ pairs: next });
  };

  // [옵션B] 팀 없는 병동 → N 으로 가분할(즉시 재미리보기). n<2 면 해제(전체로).
  const setWardTeamCount = (gid: string, n: number) => {
    const next = { ...teamCounts };
    if (n >= 2) next[gid] = n;
    else delete next[gid];
    setTeamCounts(next);
    runPreview({ teamCounts: next });
  };

  const { mutate: runApply, isPending: applying } = useMutation({
    mutationFn: async () => {
      const [y, m] = month.split("-").map(Number);
      if (isOption1 && previewO1) {
        const assignments = Object.entries(previewO1.teams).flatMap(
          ([tid, ids]) => ids.map((id) => ({ nurse_id: id, team_id: Number(tid) }))
        );
        const r = await httpClient.post<{ created: number }>(
          "/teams/classify/apply",
          {
            year: y,
            month: m,
            group_id: selected[0],
            assignments,
            pair_decisions: pairDecisions,
            note: `원티드 팀분류 ${month}`,
          }
        );
        return { kind: "o1" as const, r };
      }
      const r = await httpClient.post<{ transfers: number; team_changes: number }>(
        "/teams/redistribute/apply",
        {
          group_ids: selected,
          year: y,
          month: m,
          assignments: buildRedistributeAssignments(previewO2!.wards),
          note: `원티드 병동재분배 ${month}`,
        }
      );
      return { kind: "o2" as const, r };
    },
    onSuccess: (res) => {
      const [year, monthNumber] = month.split("-").map(Number);
      if (res.kind === "o1") {
        toast.success(`${res.r?.created ?? 0}명 ${month} 발효 예약 (즉시 변경 아님)`);
      } else {
        toast.success(
          `이동 ${res.r?.transfers ?? 0}건 · 팀변경 ${
            res.r?.team_changes ?? 0
          }건 ${month} 발효 예약`
        );
      }
      selected.forEach((groupId) => {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.nurses.list(groupId, year, monthNumber),
        });
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.nurses.members(groupId, year, monthNumber),
        });
      });
      onClose();
    },
    onError: () => toast.error("적용에 실패했습니다."),
  });

  const canPreview = selected.length >= 1 && !previewing;
  const modeLabel =
    selected.length === 0
      ? "병동을 선택하세요"
      : isOption1
        ? "병동 내 팀 재분류 (옵션1)"
        : "병동 간 재분배 (옵션2)";

  return (
    <CustomModal.Panel
      onClose={onClose}
      header
      headerTitle="원티드 기반 재배치"
      button
      btnCancelText="닫기"
      btnCancelOnClick={onClose}
      btnSuccessText={`${month}부터 적용`}
      btnSuccessOnClick={() => runApply()}
      btnSuccessDisabled={!hasPreview || applying}
    >
      <div className="flex max-h-[70vh] w-[640px] flex-col gap-4 overflow-y-auto p-1 text-sm">
        {/* 1. 병동 선택 */}
        <section className="rounded-md border border-slate-200 p-3">
          <p className="mb-2 font-medium text-slate-700">
            병동 선택{" "}
            <span className="text-xs font-normal text-slate-400">
              (1개=팀 재분류 / 2개+=병동 재분배)
            </span>
          </p>
          <div className="flex flex-wrap gap-2">
            {wardOptions.map((g) => (
              <label
                key={g.group_id}
                className={`flex cursor-pointer items-center gap-1.5 rounded border px-2.5 py-1 ${
                  selected.includes(g.group_id)
                    ? "border-blue-400 bg-blue-50 text-blue-700"
                    : "border-slate-300 text-slate-600"
                }`}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(g.group_id)}
                  onChange={() => toggleWard(g.group_id)}
                />
                {g.group_name}
              </label>
            ))}
          </div>
          <p className="mt-2 text-xs font-medium text-blue-600">▸ {modeLabel}</p>
        </section>

        {/* 2. 월 + (옵션2) 정원 모드 */}
        <section className="flex flex-wrap items-center gap-3 rounded-md border border-slate-200 p-3">
          <span className="text-slate-600">대상 월</span>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="rounded border border-slate-300 px-2 py-1"
          />
          {!isOption1 && selected.length >= 2 && (
            <>
              <span className="ml-2 text-slate-600">정원</span>
              <label className="flex items-center gap-1">
                <input
                  type="radio"
                  checked={mode === "even"}
                  onChange={() => setMode("even")}
                />
                균등
              </label>
              <label className="flex items-center gap-1">
                <input
                  type="radio"
                  checked={mode === "explicit"}
                  onChange={() => setMode("explicit")}
                />
                그룹별 지정
              </label>
            </>
          )}
        </section>

        {/* 2-1. (옵션2) explicit 목표 인원 */}
        {!isOption1 && mode === "explicit" && selected.length >= 2 && (
          <section className="flex flex-wrap gap-3 rounded-md border border-slate-200 p-3">
            {selected.map((gid) => {
              const g = wardOptions.find((x) => x.group_id === gid);
              return (
                <label key={gid} className="flex items-center gap-1.5">
                  <span className="text-slate-600">{g?.group_name}</span>
                  <input
                    type="number"
                    min={0}
                    value={targetSizes[gid] ?? ""}
                    onChange={(e) =>
                      setTargetSizes((s) => ({ ...s, [gid]: Number(e.target.value) }))
                    }
                    className="w-16 rounded border border-slate-300 px-2 py-1"
                  />
                  명
                </label>
              );
            })}
          </section>
        )}

        <div>
          <button
            onClick={() => runPreview()}
            disabled={!canPreview}
            className="rounded bg-blue-500 px-4 py-1.5 text-white hover:bg-blue-600 disabled:opacity-50"
          >
            {previewing ? "분석 중…" : "미리보기"}
          </button>
        </div>

        {/* 422: 시니어 지정 유도 (옵션2) */}
        {needsG1 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-700">
            <div>
              다음 병동에 시니어(Grade-1)가 없습니다 —{" "}
              <b>{needsG1.map((w) => w.name || w.group_id).join(", ")}</b>. 시니어를
              먼저 지정하거나, 시니어 없이 그대로 진행할 수 있습니다.
            </div>
            <button
              type="button"
              onClick={() => {
                setAllowMissingG1(true);
                runPreview({ allowMissingG1: true });
              }}
              className="mt-2 rounded border border-amber-400 bg-white px-3 py-1 text-sm font-medium text-amber-700 hover:bg-amber-100"
            >
              시니어 없이 진행
            </button>
          </div>
        )}

        {/* 결과 — 옵션1 */}
        {previewO1 && (
          <section className="flex flex-col gap-2">
            {(previewO1.warnings?.length ?? 0) > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-700">
                {previewO1.warnings!.map((w, i) => (
                  <div key={i}>⚠ {w}</div>
                ))}
              </div>
            )}
            <div className="text-slate-500">
              풀 {previewO1.num_pool}명 · N전담 제외 {previewO1.num_excluded_night} ·
              변경 제안 <b className="text-slate-700">{previewO1.num_changed}</b>명
            </div>

            {/* 참여 인원 rows + 근무 컬럼 (미참여=미지정). allowed_shifts []=전체 */}
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="font-medium text-slate-600">
                  참여 인원 (해제 시 미지정) · 참여 {partCount()}
                </span>
                <button
                  onClick={() => runPreview()}
                  disabled={previewing}
                  className="rounded border border-blue-300 px-2 py-0.5 text-xs text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                >
                  {previewing ? "분석 중…" : "선택 반영"}
                </button>
              </div>
              <RosterRows
                members={o1Roster}
                isOn={(n) => isParticipant(n.nurse_id, n.is_night)}
                onToggle={toggleParticipant}
              />
            </div>

            {/* 프리셉터 짝 유지/해제 */}
            {(previewO1.pairs?.length ?? 0) > 0 && (
              <div className="rounded border border-slate-200 bg-white p-2">
                <p className="mb-1 font-medium text-slate-600">프리셉터 짝</p>
                {previewO1.pairs!.map((p) => {
                  const released = pairDecisions[p.preceptee_id] === "release";
                  return (
                    <div
                      key={p.preceptee_id}
                      className="flex items-center gap-2 text-xs"
                    >
                      <span className="text-slate-600">
                        {p.preceptee_name} ↔ {p.preceptor_name}
                      </span>
                      <span
                        className={`rounded px-1.5 py-0.5 ${
                          released
                            ? "bg-slate-100 text-slate-400"
                            : p.status === "split"
                              ? "bg-amber-50 text-amber-600"
                              : "bg-emerald-50 text-emerald-600"
                        }`}
                      >
                        {released ? "해제됨" : p.status === "split" ? "갈라짐" : "같은 팀"}
                      </span>
                      <button
                        onClick={() =>
                          setPairDecision(p.preceptee_id, released ? "keep" : "release")
                        }
                        className="ml-auto rounded border border-slate-300 px-2 py-0.5 text-slate-600 hover:bg-slate-100"
                      >
                        {released ? "되돌리기" : "해제"}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* 제안 팀 */}
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(previewO1.teams).map(([tid, ids]) => (
                <div key={tid} className="rounded-md border border-slate-200 p-3">
                  <p className="mb-1 font-medium text-slate-700">
                    {tid}팀 ({ids.length})
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {ids.map((id) => (
                      <NameChip
                        key={id}
                        name={nameById[id] ?? id}
                        grade={gradeById[id]}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {previewO1.unassigned.length > 0 && (
              <p className="text-xs text-slate-400">
                미지정: {previewO1.unassigned.map((u) => u.name ?? u.nurse_id).join(", ")}
              </p>
            )}
          </section>
        )}

        {/* 결과 — 옵션2 */}
        {previewO2 && (
          <section className="flex flex-col gap-2">
            {previewO2.warnings?.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-700">
                {previewO2.warnings.map((w, i) => (
                  <div key={i}>⚠ {w}</div>
                ))}
              </div>
            )}
            <div className="text-slate-500">
              풀 {previewO2.num_pool}명 · N전담 제외 {previewO2.num_excluded_night}
              {(previewO2.num_excluded_overlap ?? 0) > 0 &&
                ` · 기간겹침 제외 ${previewO2.num_excluded_overlap}`}
              {previewO2.num_participants != null &&
                ` · 참여 ${previewO2.num_participants}`}{" "}
              · 이동 제안 <b className="text-slate-700">{previewO2.num_moved}</b>명
            </div>

            {/* 참여 인원 (미참여=현재 병동 고정). 🔒=기간겹침 */}
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="font-medium text-slate-600">
                  참여 인원 (체크 해제 시 현재 병동 유지)
                </span>
                <button
                  onClick={() => runPreview()}
                  disabled={previewing}
                  className="rounded border border-blue-300 px-2 py-0.5 text-xs text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                >
                  {previewing ? "분석 중…" : "선택 반영"}
                </button>
              </div>
              <RosterRows
                members={o2Roster}
                isOn={(n) => isParticipant(n.nurse_id, n.is_night)}
                onToggle={toggleParticipant}
                wardName={wardNameById}
              />
              {(previewO2.excluded_overlap ?? []).length > 0 && (
                <p className="mt-1.5 flex flex-wrap gap-1 text-xs text-amber-600">
                  <span className="text-slate-400">기간겹침 제외:</span>
                  {(previewO2.excluded_overlap ?? []).map((e) => (
                    <span key={e.nurse_id}>🔒 {e.name ?? e.nurse_id}</span>
                  ))}
                </p>
              )}
              {(previewO2.fixed_stay ?? []).length > 0 && (
                <p className="mt-1.5 text-xs text-slate-400">
                  현재 병동 유지:{" "}
                  {(previewO2.fixed_stay ?? [])
                    .map((f) => f.name ?? f.nurse_id)
                    .join(", ")}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              {Object.entries(previewO2.wards).map(([gid, w]) => {
                // 팀 미정의(모든 버킷 team_id=null) → N개로 나누기 컨트롤 노출.
                //   가분할 후에도 버킷은 그대로 team_id=null 이므로 입력창이 유지돼 N 재입력 가능.
                const buckets = Object.values(w.teams);
                const isTeamless =
                  buckets.length > 0 && buckets.every((b) => b.team_id == null);
                const curN = teamCounts[gid] ?? 0;
                return (
                  <div
                    key={gid}
                    className="rounded-md border border-slate-200 p-3"
                  >
                    <div className="mb-1 flex items-center justify-between">
                      <p className="font-medium text-slate-700">
                        {w.name} ({w.size})
                      </p>
                      {isTeamless && (
                        <label className="flex items-center gap-1 text-xs text-slate-500">
                          팀
                          <input
                            type="number"
                            min={2}
                            max={Math.max(2, w.size)}
                            value={curN || ""}
                            placeholder="N"
                            className="w-12 rounded border border-slate-300 px-1 py-0.5 text-center"
                            onChange={(e) =>
                              setWardTeamCount(gid, Number(e.target.value))
                            }
                          />
                          개로 나누기
                        </label>
                      )}
                    </div>
                    {Object.entries(w.teams).map(([label, bucket]) => (
                      <div key={label} className="mb-1">
                        <span className="text-xs text-slate-400">
                          {label === "전체" ? "전체" : `${label}팀`}
                          {bucket.team_id == null && label !== "전체" && (
                            <span className="ml-1 text-amber-500">(신규)</span>
                          )}
                        </span>
                        <div className="flex flex-wrap gap-1">
                          {bucket.members.map((m) => (
                            <NameChip
                              key={m.nurse_id}
                              name={m.name ?? m.nurse_id}
                              grade={gradeById[m.nurse_id]}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </CustomModal.Panel>
  );
}
